import { Call, Device } from "@twilio/voice-sdk";
import type { IExtendedDeviceOptions } from "@twilio/voice-sdk/es5/twilio/device";
import packageInfo from "package.json";
import { ClientEventEmitter } from "~/modules/client/ClientEventEmitter/ClientEventEmitter";
import { getConfigRegion } from "~/utils/regionUtil";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { VoiceController } from "~/modules/voice/VoiceController";
import { ClientEvent } from "~/modules/client";
import { VoiceOptions } from "~/modules/voice/voiceOptions";

export class VoiceControllerImpl implements VoiceController {
    readonly #session: SessionImpl;

    readonly #clientOptions: ClientOptionsStore;

    readonly #logger: Logger;

    readonly #ctx: ContextManager;

    #voiceOptions?: VoiceOptions;

    #voiceDevice: Device;

    #initializationPromise?: Promise<Device | undefined>;

    #eventListenersRegistered: boolean = false;

    constructor(ctx: ContextManager) {
        this.#session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.#logger = getLogger(ctx)(LoggerName.Voice);
        this.#ctx = ctx;
        this.#logger.debug("VoiceController constructed");
    }

    async init(voiceDevice?: Device): Promise<Device | undefined> {
        this.#voiceOptions = this.#clientOptions.voiceOptions;

        if (voiceDevice) {
            this.#voiceDevice = voiceDevice;
            this.#setUpEventHandlers();

            this.#registerClientEventListeners();
            return Promise.resolve(this.#voiceDevice);
        }

        if (this.#initializationPromise) {
            return this.#initializationPromise;
        }

        this.#initializationPromise = (async (): Promise<Device | undefined> => {
            try {
                this.#registerClientEventListeners();

                const configRegion = getConfigRegion(this.#clientOptions);

                const formattedConfigRegion = this.#formatRegionValue(configRegion);

                const chunderWUrl = `chunderw-vpc-gll${formattedConfigRegion}.twilio.com`;
                const eventGWUrl = `eventgw.twilio.com`;

                const sdkVersion = packageInfo.version;
                const voiceDeviceOptions: IExtendedDeviceOptions = {
                    allowIncomingWhileBusy: true,
                    appName: "flex-sdk",
                    appVersion: sdkVersion,
                    logLevel: this.#clientOptions?.logger?.level,
                    chunderw: chunderWUrl,
                    eventgw: eventGWUrl
                };

                this.#voiceDevice = await this.#createAndRegisterDevice(this.#session.token, voiceDeviceOptions);
                const incomingSoundStatus = this.voiceDevice.audio?.incoming(false);
                this.#logger.debug(`Incoming sound status for voice: ${incomingSoundStatus}`);
                this.#setUpEventHandlers();
                return Promise.resolve(this.#voiceDevice);
            } catch (error) {
                this.#initializationPromise = undefined;
                this.#logger.error(error);
                const errorToReject = error instanceof Error ? error : new Error(String(error));
                return Promise.reject(errorToReject);
            }
        })();

        return this.#initializationPromise;
    }

    readonly #onClientDestroyed = (): void => {
        if (this.#voiceDevice) {
            this.#voiceDevice.destroy();
        }
    };

    subscribeToIncomingCallEvent = (callback: (call: Call) => void): void => {
        if (!this.#voiceDevice) {
            throw new Error("Voice device is not initialized");
        }

        this.#voiceDevice.on(Device.EventName.Incoming, callback);
    };

    unsubscribeFromIncomingCallEvent = (callback: (call: Call) => void): void => {
        if (!this.#voiceDevice) {
            throw new Error("Voice device is not initialized");
        }

        this.#voiceDevice.removeListener(Device.EventName.Incoming, callback);
    };

    #setUpEventHandlers(): void {
        
        this.#voiceDevice.removeListener(Device.EventName.Incoming, this.#handleIncomingCall);
        this.#voiceDevice.removeListener(Device.EventName.Error, this.#onDeviceError);

        
        this.#voiceDevice.on(Device.EventName.Incoming, this.#handleIncomingCall);
        this.#voiceDevice.on(Device.EventName.Error, this.#onDeviceError);
    }

    readonly #handleIncomingCall = (call: Call): void => {
        this.#onIncomingCall(call);
    };

    #setUpCallEventHandlers(call: Call): void {
        call.on("accept", this.#onCallStatusChange);
        call.on("cancel", this.#onCallStatusChange);
        call.on("disconnect", this.#onCallStatusChange);
    }

    #removeCallEventHandlers(call: Call): void {
        call.off("accept", this.#onCallStatusChange);
        call.off("cancel", this.#onCallStatusChange);
        call.off("disconnect", this.#onCallStatusChange);
    }

    readonly #onIncomingCall = (call: Call): void => {
        this.#logger.debug("Incoming voice call in VoiceController", call);

        this.#setUpCallEventHandlers(call);
        if (!this.#voiceOptions?.autoAcceptIncomingCalls) {
            return;
        }
        try {
            if (!call.customParameters) {
                this.#logger.debug("Call has no custom parameters");
                call.accept();
                return;
            }

            const sessionIdentifier = call.customParameters.get("flex_session_identifier");
            const currentSessionId = this.#session?.flexSessionIdentifier;

            if (!sessionIdentifier) {
                this.#logger.debug("Call has no session identifier");
                call.accept();
                return;
            }

            if (sessionIdentifier === currentSessionId) {
                call.accept();
            } else {
                this.#logger.debug(
                    "Call was accepted from a different session ID, delaying acceptance from current session"
                );
                setTimeout(() => {
                    if (call.status() === Call.State.Pending) {
                        call.accept();
                    }
                }, 5000);
            }
        } catch (error) {
            this.#logger.error("Error handling incoming call", error);
        }
    };

    readonly #onCallStatusChange = (call: Call): void => {
        this.#logger.debug("Call status changed in VoiceController", call);
        const callStatus = call?.status();
        this.#logger.debug("Voice call status changed, call status", callStatus);
        if (callStatus === Call.State.Closed) {
            this.#removeCallEventHandlers(call);
        }
    };

    readonly #onDeviceError = (error: Error, call: Call): void => {
        this.#logger.error("Voice device error", error, call);
    };

    readonly #onNewToken = (token: string): void => {
        if (!this.#voiceDevice) {
            this.#logger.warn("onNewToken in VoiceController, but there is no voiceDevice");
        }

        this.#voiceDevice.updateToken(token);
    };

    get voiceDevice(): Device {
        return this.#voiceDevice;
    }

    isAudioInputDeviceAvailable(): boolean {
        if (!this.#voiceDevice) {
            this.#logger.error("Voice device is not initialized");
            return false;
        }
        this.#logger.debug("Current device", this.#voiceDevice);
        this.#logger.debug("Available input devices:", this.#voiceDevice.audio?.availableInputDevices);

        const ids: Array<string> = Array.from(this.#voiceDevice.audio?.availableInputDevices.keys() ?? []);
        if (!ids.length) {
            this.#logger.error("No audio input device");
            return false;
        }
        this.#logger.debug("Available input device IDs:", ids);
        const activeDeviceID = ids.find((id) => id === "default") ?? ids[0];
        this.#logger.debug(`Active device ID: ${activeDeviceID}`);
        return true;
    }

    


    #registerClientEventListeners(): void {
        if (this.#eventListenersRegistered) return;
        this.#logger.debug("Registering client event listeners");

        const clientEventEmitter = this.#ctx.retrieveInstanceOf(ContextInstance.ClientEventEmitter, ClientEventEmitter);
        clientEventEmitter.addListener(ClientEvent.TokenUpdated, this.#onNewToken);
        clientEventEmitter.addListener(ClientEvent.ClientDestroyed, this.#onClientDestroyed);
        this.#eventListenersRegistered = true;
    }

    




    async #createAndRegisterDevice(token: string, options: IExtendedDeviceOptions): Promise<Device> {
        const device = new Device(token, options);
        await device.register();

        if (device.state === Device.State.Registered) {
            this.#logger.info("Voice device is already registered");
            return device;
        }

        return new Promise<Device>((resolve, reject) => {
            const timeout = setTimeout(() => {
                const errorMsg = "Voice device registration timed out";
                this.#logger.error(errorMsg);
                reject(new Error(errorMsg));
            }, 10_000);

            const eventHandler = (eventDevice: Device) => {
                if (eventDevice.state === Device.State.Registered) {
                    this.#logger.info("Voice device registered successfully");
                    clearTimeout(timeout);
                    device.off(Device.EventName.Registered, eventHandler);
                    resolve(eventDevice);
                }
            };

            device.on(Device.EventName.Registered, eventHandler);
        });
    }

    #formatRegionValue(inputString: string = ""): string {
        const trimmedInput = inputString.trim();

        // Return empty string if input is empty after trimming
        if (!trimmedInput) return "";

        if (trimmedInput === "stage" || trimmedInput === "dev") {
            return `.${trimmedInput}`;
        }

        const dashIndex = trimmedInput.indexOf("-");

        if (dashIndex > 0) {
            
            const prefix = trimmedInput.substring(0, dashIndex).trim();
            return prefix ? `.${prefix}` : "";
        }

        
        return `-${trimmedInput}`;
    }

    destroy(): void {
        this.voiceDevice?.destroy();
    }
}
